// src/pages/NodesTab.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { SectionHeader } from '../components/Nav';

const richStyles = `
  .nodes-rich h2 { font-size:17px; font-weight:600; margin:10px 0 6px; font-family:'Playfair Display',serif; color:var(--sand-text); }
  .nodes-rich h3 { font-size:14px; font-weight:600; margin:8px 0 4px; font-family:'Playfair Display',serif; color:var(--sand-text); }
  .nodes-rich h4 { font-size:12px; font-weight:600; margin:6px 0 3px; font-family:'Playfair Display',serif; color:var(--sand-text); text-transform:uppercase; letter-spacing:0.5px; }
  .nodes-rich p  { margin:0 0 12px; min-height:1.4em; font-size:13px; line-height:1.7; color:var(--sand-muted); }
  .nodes-rich a  { color:#38bdf8; text-decoration:underline; }
  .nodes-rich strong { font-weight:600; color:var(--sand-text); }
  .nodes-rich em { font-style:italic; }
  .nodes-rich img { max-width:100%; border-radius:6px; margin:8px 0; display:block; }
  .nodes-rich iframe { width:100%; aspect-ratio:16/9; height:auto; border-radius:6px; margin:8px 0; display:block; }
`;

function RichOrPlain({ content, className }) {
  if (!content) return null;
  const isHTML = content.trim().startsWith('<');
  if (isHTML) return (
    <>
      <style>{richStyles}</style>
      <div className={className} dangerouslySetInnerHTML={{ __html: content }} />
    </>
  );
  return <p style={{ fontSize: 13, color: 'var(--sand-muted)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{content}</p>;
}

// Convert node name to a safe anchor id
function toAnchor(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function scrollToNode(name) {
  const id = 'node-' + toAnchor(name);
  const el = document.getElementById(id);
  if (!el) return;
  // Offset for sticky admin header (90px) + a little breathing room
  const y = el.getBoundingClientRect().top + window.scrollY - 110;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export default function NodesTab() {
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'config', 'nodes'));
        if (snap.exists()) setConfig(snap.data());
      } catch (e) {
        console.error('Failed to load nodes config:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    if (!hash) return;
    const targetId = hash.replace('#', '');
    // Retry a few times to handle slow renders
    let attempts = 0;
    function tryScroll() {
      const el = document.getElementById(targetId);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: 'smooth' });
      } else if (attempts < 5) {
        attempts++;
        setTimeout(tryScroll, 200);
      }
    }
    const timer = setTimeout(tryScroll, 150);
    return () => clearTimeout(timer);
  }, [loading]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--sand-hint)', fontStyle: 'italic', fontSize: 13 }}>Loading…</div>
  );

  const repoUrl     = config?.repoUrl     || 'https://github.com/axces2000/comfyui-axces2000';
  const repoName    = config?.repoName    || 'comfyui-axces2000';
  const author      = config?.authorHandle || 'axces2000';
  const description = config?.description  || '';
  const images      = config?.images       || [];
  const nodes       = config?.nodes        || [];

  return (
    <div className="fade-up">
      <SectionHeader title="Custom Nodes" />

      {/* ── Repo card ── */}
      <div style={{ background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-lg)', padding: '24px 28px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: description ? 16 : 0 }}>
          {/* GitHub icon */}
          <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', background: 'var(--sand-bg)', border: '1px solid var(--sand-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="var(--sand-text)">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--sand-muted)' }}>{author} /</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 500, color: 'var(--sand-text)' }}>{repoName}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, padding: '2px 8px', borderRadius: 20, background: 'var(--sand-teal-bg)', color: '#5eead4', border: '0.5px solid var(--sand-teal-border)' }}>public</span>
            </div>
            <RichOrPlain content={description} className="nodes-rich" />
          </div>
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href={repoUrl} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, padding: '10px 20px', borderRadius: 'var(--radius-sm)', background: 'var(--sand-text)', color: 'var(--sand-bg)', textDecoration: 'none', transition: 'opacity 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
            View on GitHub ↗
          </a>
          <a href={`${repoUrl}/archive/refs/heads/main.zip`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, padding: '10px 20px', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--sand-amber)', textDecoration: 'none', border: '1px solid var(--sand-amber-border)' }}
          >Download ZIP ↓</a>
        </div>
      </div>

      {/* ── Table of contents ── */}
      {nodes.length > 0 && (
        <div style={{ background: 'var(--sand-surface)', border: '1px solid var(--sand-amber-border)', borderRadius: 'var(--radius-lg)', padding: '18px 24px', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 600, color: 'var(--sand-text)' }}>
              Nodes in this package
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--sand-hint)', marginLeft: 4 }}>
              {nodes.length} total
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {nodes.map((node, i) => (
              <button
                key={node.id}
                onClick={() => scrollToNode(node.name)}
                style={{
                  fontFamily: "'Source Serif 4', serif", fontSize: 13,
                  padding: '8px 18px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--sand-amber)', color: '#0e1525',
                  border: 'none',
                  cursor: 'pointer', transition: 'opacity 0.15s',
                  fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, opacity: 0.6 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                {node.name || `Node ${i + 1}`}
                <span style={{ fontSize: 10, opacity: 0.6 }}>↓</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Individual node sections ── */}
      {nodes.length > 0 && (
        <div>
          {nodes.map((node, i) => (
            <div
              key={node.id}
              id={'node-' + toAnchor(node.name)}
              style={{ marginBottom: 32, scrollMarginTop: 110 }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600, color: 'var(--sand-text)' }}>
                  {node.name || `Node ${i + 1}`}
                </h3>
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  title="Back to top"
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--sand-hint)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, letterSpacing: 0.5 }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--sand-amber)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--sand-hint)'}
                >↑ top</button>
              </div>

              <div style={{ background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-lg)', padding: '18px 22px' }}>
                {node.imageUrl && (
                  <img
                    src={node.imageUrl}
                    alt={node.name}
                    style={{ width: '100%', borderRadius: 'var(--radius-md)', marginBottom: 16, display: 'block' }}
                  />
                )}
                <RichOrPlain content={node.description} className="nodes-rich" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Install instructions ── */}
      <div style={{ background: 'var(--sand-surface)', border: '0.5px solid var(--sand-border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 24 }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)', marginBottom: 14 }}>Installation</h3>
        <ol style={{ paddingLeft: 18, fontSize: 13, color: 'var(--sand-muted)', lineHeight: 2 }}>
          <li>Open <strong style={{ color: 'var(--sand-text)' }}>ComfyUI Manager</strong> in your ComfyUI interface</li>
          <li>Go to <strong style={{ color: 'var(--sand-text)' }}>Custom Nodes → Install via Git URL</strong></li>
          <li>Paste: <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'var(--sand-bg)', padding: '2px 6px', borderRadius: 4, color: 'var(--sand-amber)' }}>{repoUrl}.git</code></li>
          <li>Restart ComfyUI</li>
        </ol>
      </div>

      {/* ── Legacy gallery (if any) ── */}
      {images.length > 0 && (
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: 'var(--sand-text)', marginBottom: 14 }}>Gallery</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {images.map((src, i) => {
              const isVideo = /\.(mp4|webm|mov)/.test(src.split('?')[0].toLowerCase());
              return (
                <div key={i} style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '0.5px solid var(--sand-border)', height: 220, flexShrink: 0 }}>
                  {isVideo
                    ? <video src={src} controls style={{ height: '100%', width: 'auto', display: 'block', background: '#000' }} />
                    : <img src={src} alt={`Node preview ${i + 1}`} style={{ height: '100%', width: 'auto', display: 'block' }} />
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
